import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { de } from './locales/de';
import { en } from './locales/en';
import { pl } from './locales/pl';
import { vi } from './locales/vi';
import { DE } from './errors/de';
import { EN } from './errors/en';
import { PL } from './errors/pl';
import { VI } from './errors/vi';

export type Locale = 'en' | 'vi' | 'de' | 'pl';

/** `country` is an ISO-3166 alpha-2 code for `country-flag-icons` SVGs. */
export const LOCALES: {
  value: Locale;
  label: string;
  native: string;
  flag: string;
  country: 'GB' | 'VN' | 'DE' | 'PL';
}[] = [
  { value: 'en', label: 'English', native: 'English', flag: '🇬🇧', country: 'GB' },
  { value: 'vi', label: 'Vietnamese', native: 'Tiếng Việt', flag: '🇻🇳', country: 'VN' },
  { value: 'de', label: 'German', native: 'Deutsch', flag: '🇩🇪', country: 'DE' },
  { value: 'pl', label: 'Polish', native: 'Polski', flag: '🇵🇱', country: 'PL' },
];

const MESSAGES = { en, vi, de, pl } as const;
const ERROR_MESSAGES = { en: EN, vi: VI, de: DE, pl: PL } as const;

interface I18nStore {
  locale: Locale;
  localePending: boolean;
  setLocale: (locale: Locale) => void;
  adoptLocale: (locale: Locale) => void;
  markLocaleSynced: () => void;
}

export const useI18nStore = create<I18nStore>()(
  persist(
    (set) => ({
      locale:
        (navigator.language.slice(0, 2) as Locale) in MESSAGES
          ? (navigator.language.slice(0, 2) as Locale)
          : 'en',
      localePending: false,
      setLocale: (locale) => set({ locale, localePending: true }),
      adoptLocale: (locale) => set({ locale, localePending: false }),
      markLocaleSynced: () => set({ localePending: false }),
    }),
    {
      name: 'restaurant-locale',
      storage: createJSONStorage(() => localStorage),
      version: 1,
      migrate: (persisted, version) =>
        (version === 0
          ? { ...(persisted as Partial<I18nStore>), localePending: true }
          : persisted) as I18nStore,
    },
  ),
);

export function useT() {
  const locale = useI18nStore((s) => s.locale);
  return MESSAGES[locale] ?? MESSAGES.en;
}

/** Same lookup as `useT`, but callable from plain functions outside a
 *  component (e.g. a module-level error handler) via the store's current
 *  state instead of the hook. */
export function getT() {
  const locale = useI18nStore.getState().locale;
  return MESSAGES[locale] ?? MESSAGES.en;
}

/** Localized error-code → message map for the current locale (pairs with
 *  `resolveErrorText` in api/errors.ts). */
export function useErrorT() {
  const locale = useI18nStore((s) => s.locale);
  return ERROR_MESSAGES[locale] ?? ERROR_MESSAGES.en;
}

/** Same map as `useErrorT`, for callers outside React (api/query-client.ts).
 *  Reads the store the way `getT` does — the handler used to re-parse
 *  localStorage and branch by hand, and silently missed `pl`. */
export function getErrorT() {
  const locale = useI18nStore.getState().locale;
  return ERROR_MESSAGES[locale] ?? ERROR_MESSAGES.en;
}
